import { useAppDispatch } from '../../../../redux/hooks'
import React, { useCallback, useContext, useMemo } from 'react'
import { Chip, ScreenContext } from 'react-components'
import { useTranslation } from '../../../../hooks/locale'
import { useSelector } from 'react-redux'
import { filterClick, getCurrentOpenedFilterModal, pushModal, popModal } from '../../../../redux/filters'
import { FilterModalType } from '../../filter-sort-section/types'
import { FilterSectionTransport } from '../../../../transports/search-result-listing-static-transport'

import SortModal from '../sort-modal'
import SortContent from '../../filter-sort-section/sort-section/sort-content'
import { getRelativeURL } from '../../../../utils/encoding-utils'
import { getTruncatedTextByChars } from '../../../../utils/miscellaneous-utils'
import { useRequestUserConfigContext } from '../../../../hooks/request'
import { useOnClickOutside } from '../../../../hooks/dom'
import { ChipWrapper, Container, DesktopSortContainer, SortIcon, StyledChevron } from './styles'
import { useActiveSortOption } from '../hooks'
import { stopPropagation } from '../../../../utils/dom-utils'

interface Props {
    filterStaticData: FilterSectionTransport
}

const SortPill: React.FC<Props> = ({ filterStaticData }) => {
    const dispatch = useAppDispatch()
    const { isMobile, isTablet } = useContext(ScreenContext)
    const { t } = useTranslation()
    const user = useRequestUserConfigContext()
    const currentOpenedFilterModal = useSelector(getCurrentOpenedFilterModal)
    const { activeSortOption, isNonDefaultSortActive } = useActiveSortOption()

    const isSortOpen = currentOpenedFilterModal === FilterModalType.SORT
    const isMobileOrTablet = isMobile || isTablet

    const handleModalToggle = useCallback(() => {
        if (currentOpenedFilterModal === FilterModalType.SORT) {
            dispatch(popModal())
        } else {
            dispatch(pushModal(FilterModalType.SORT))
        }
    }, [currentOpenedFilterModal, dispatch])

    const handleSortSubmit = useCallback(
        (link: string) => {
            const encodedLink = getRelativeURL(link)
            dispatch(filterClick(encodedLink, true, user))
            dispatch(popModal())
        },
        [dispatch, user],
    )

    const closeDropdown = useCallback(() => {
        if (isSortOpen && !isMobileOrTablet) {
            dispatch(popModal())
        }
    }, [isSortOpen, isMobileOrTablet, dispatch])

    const { ref: containerRef } = useOnClickOutside<HTMLDivElement>(closeDropdown)

    const activeSortLabelKey = activeSortOption ? `${t(activeSortOption.label)}` : ''

    const sortPillLabel = useMemo(() => {
        const label = isNonDefaultSortActive
            ? `${t('mobileFilter.sort')}: ${activeSortLabelKey}`.trim()
            : t('mobileFilter.sort')

        return getTruncatedTextByChars(label, 20)
    }, [isNonDefaultSortActive, activeSortLabelKey, t])

    return (
        <Container ref={containerRef} onMouseDown={stopPropagation}>
            <ChipWrapper>
                <Chip
                    onClick={handleModalToggle}
                    label={sortPillLabel}
                    selected={isNonDefaultSortActive}
                    icon={isMobileOrTablet ? <SortIcon /> : <StyledChevron $isOpen={isSortOpen} />}
                    size="small"
                />
            </ChipWrapper>
            <DesktopSortContainer isOpen={isSortOpen}>
                <SortContent
                    className="sort-content"
                    handleSortSubmit={handleSortSubmit}
                    isModal
                    isSortOpen={isSortOpen}
                />
            </DesktopSortContainer>
            {isMobileOrTablet && (
                <SortModal
                    handleModalToggle={handleModalToggle}
                    handleSortSubmit={handleSortSubmit}
                    filterStaticData={filterStaticData}
                />
            )}
        </Container>
    )
}

export default SortPill
